import React, { CSSProperties, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Switch,
  Tab,
  TabList,
  Tabs,
  Text,
  Textarea,
  Tooltip,
  useClipboard,
  useToast,
} from '@chakra-ui/react';
import {
  convertRoll20ChatEffect,
  convertRoll20ChatEffectPreview,
  SpeechMode,
  StyledSegment,
  TimeMode,
} from './converter';
import './App.css';

const SAMPLE_TEXT = `코마에다, 반론이 있다면 말해줘 ···
솔직히, 나도 믿고싶지 않아 ···
함께 수사했던 네가 ··· 그렇게 친절했던 네가 ···
"쑥떡"을 가져간 "범인"이라니 ···`;

const UI_THEME_BY_TIME: Record<
  TimeMode,
  {
    accent: string;
    accentHover: string;
    accentActive: string;
    accentText: string;
    accentRgb: string;
    pageBg: string;
    shellGradientEnd: string;
    panelBg: string;
    panelBorder: string;
    controlBg: string;
    controlActiveBg: string;
    inputBg: string;
    resultBg: string;
    text: string;
    headingText: string;
    mutedText: string;
    subtleText: string;
    pageHeadingText: string;
    pageMutedText: string;
    pageSubtleText: string;
    border: string;
  }
> = {
  day: {
    accent: '#fec701',
    accentHover: '#ffd84e',
    accentActive: '#d9a900',
    accentText: '#16120d',
    accentRgb: '254, 199, 1',
    pageBg: '#f2fefd',
    shellGradientEnd: '#e0f5fd',
    panelBg: 'rgba(42, 36, 30, 0.78)',
    panelBorder: '#6f6657',
    controlBg: '#181512',
    controlActiveBg: '#26211c',
    inputBg: '#201c18',
    resultBg: '#181512',
    text: 'white',
    headingText: 'white',
    mutedText: '#d7d1c4',
    subtleText: '#a89f91',
    pageHeadingText: '#16120d',
    pageMutedText: '#4f5d5d',
    pageSubtleText: '#5f6d6d',
    border: '#7a6e5d',
  },
  night: {
    accent: '#008ffe',
    accentHover: '#35a8ff',
    accentActive: '#0072cb',
    accentText: 'white',
    accentRgb: '0, 143, 254',
    pageBg: '#012a40',
    shellGradientEnd: '#001a2a',
    panelBg: 'rgba(42, 36, 30, 0.78)',
    panelBorder: '#6f6657',
    controlBg: '#181512',
    controlActiveBg: '#26211c',
    inputBg: '#201c18',
    resultBg: '#181512',
    text: 'white',
    headingText: 'white',
    mutedText: '#d7d1c4',
    subtleText: '#a89f91',
    pageHeadingText: 'white',
    pageMutedText: '#d7d1c4',
    pageSubtleText: '#a89f91',
    border: '#7a6e5d',
  },
};

function styleTextToObject(styleText: string): CSSProperties {
  return styleText
    .split(';')
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .reduce<CSSProperties>((style, declaration) => {
      const [property, ...valueParts] = declaration.split(':');
      const value = valueParts.join(':').trim();

      if (!property || !value) {
        return style;
      }

      const camelProperty = property
        .trim()
        .replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase()) as keyof CSSProperties;

      return {
        ...style,
        [camelProperty]: value,
      };
    }, {});
}

function App() {
  const [mode, setMode] = useState<SpeechMode>('dialogue');
  const [timeMode, setTimeMode] = useState<TimeMode>('day');
  const [previewDarkMode, setPreviewDarkMode] = useState(false);
  const [input, setInput] = useState(SAMPLE_TEXT);
  const [output, setOutput] = useState('');
  const [previewLines, setPreviewLines] = useState<StyledSegment[][]>([]);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const toast = useToast();
  const { onCopy, hasCopied } = useClipboard(output);
  const uiTheme = UI_THEME_BY_TIME[timeMode];
  const themeVars = {
    '--accent-color': uiTheme.accent,
    '--accent-hover': uiTheme.accentHover,
    '--accent-active': uiTheme.accentActive,
    '--accent-text': uiTheme.accentText,
    '--accent-rgb': uiTheme.accentRgb,
    '--page-bg': uiTheme.pageBg,
    '--shell-gradient-end': uiTheme.shellGradientEnd,
    '--panel-bg': uiTheme.panelBg,
    '--panel-border': uiTheme.panelBorder,
    '--control-bg': uiTheme.controlBg,
    '--control-active-bg': uiTheme.controlActiveBg,
    '--input-bg': uiTheme.inputBg,
    '--result-bg': uiTheme.resultBg,
    '--theme-text': uiTheme.text,
    '--heading-text': uiTheme.headingText,
    '--muted-text': uiTheme.mutedText,
    '--subtle-text': uiTheme.subtleText,
    '--page-heading-text': uiTheme.pageHeadingText,
    '--page-muted-text': uiTheme.pageMutedText,
    '--page-subtle-text': uiTheme.pageSubtleText,
    '--theme-border': uiTheme.border,
  } as CSSProperties;

  const lineCount = useMemo(
    () => input.split(/\r?\n/).filter((line) => line.trim().length > 0).length,
    [input],
  );

  const handleConvert = () => {
    setOutput(convertRoll20ChatEffect(input, mode, timeMode));
    setPreviewLines(convertRoll20ChatEffectPreview(input, mode, timeMode));
  };

  const handleTimeModeChange = (index: number) => {
    const nextTimeMode: TimeMode = index === 0 ? 'day' : 'night';

    setTimeMode(nextTimeMode);
    setOutput(convertRoll20ChatEffect(input, mode, nextTimeMode));
    setPreviewLines(convertRoll20ChatEffectPreview(input, mode, nextTimeMode));
  };

  const handleSpeechModeChange = (index: number) => {
    const nextMode: SpeechMode = index === 0 ? 'dialogue' : 'monologue';

    setMode(nextMode);
    setOutput(convertRoll20ChatEffect(input, nextMode, timeMode));
    setPreviewLines(convertRoll20ChatEffectPreview(input, nextMode, timeMode));
  };

  const insertInputText = (text: string) => {
    const textarea = inputRef.current;
    const shouldUseCursor = textarea && document.activeElement === textarea;
    const start = shouldUseCursor ? textarea.selectionStart : input.length;
    const end = shouldUseCursor ? textarea.selectionEnd : input.length;
    const nextInput = `${input.slice(0, start)}${text}${input.slice(end)}`;
    const nextCursor = start + text.length;

    setInput(nextInput);

    window.requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const handleCopy = () => {
    if (!output) {
      toast({
        title: '복사할 변환 결과가 없습니다.',
        status: 'warning',
        duration: 1800,
        isClosable: true,
      });
      return;
    }

    onCopy();
    toast({
      title: '변환 결과를 복사했습니다.',
      status: 'success',
      duration: 1600,
      isClosable: true,
    });
  };

  return (
    <Box minH="100vh" bg={uiTheme.pageBg} color={uiTheme.text} className="app-shell" style={themeVars}>
      <Container maxW="1440px" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <Flex
          align={{ base: 'flex-start', md: 'center' }}
          justify="space-between"
          gap={4}
          mb={{ base: 6, md: 8 }}
          direction={{ base: 'column', md: 'row' }}
        >
          <Box>
            <Badge bg={uiTheme.accent} color={uiTheme.accentText} borderRadius="2px" px={2} py={1} mb={3}>
              Roll20 Markdown
            </Badge>
            <Heading
              as="h1"
              size={{ base: 'lg', md: 'xl' }}
              letterSpacing="0"
              lineHeight="1.15"
              color={uiTheme.pageHeadingText}
            >
              단간론파풍 채팅 효과 변환기
            </Heading>
            <HStack mt={3} spacing={3} color={uiTheme.pageSubtleText} fontSize="sm" flexWrap="wrap">
              <Text>
                made by{' '}
                <Box as="span" color={uiTheme.pageHeadingText} fontWeight="800">
                  레타
                </Box>
              </Text>
              <HStack spacing={1}>
                <Box
                  as="svg"
                  viewBox="0 0 24 24"
                  boxSize="1.12em"
                  p="0.14em"
                  bg="#000"
                  color="white"
                  borderRadius="3px"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817-5.966 6.817H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
                </Box>
                <Text
                  as="a"
                  href="https://x.com/leta_dg"
                  target="_blank"
                  rel="noopener noreferrer"
                  color={uiTheme.pageSubtleText}
                  fontWeight="700"
                  textDecoration="underline"
                  textUnderlineOffset="3px"
                  _hover={{ color: uiTheme.accent }}
                >
                  @leta_dg
                </Text>
              </HStack>
              <HStack spacing={1}>
                <Box
                  as="svg"
                  viewBox="0 0 24 24"
                  boxSize="1.12em"
                  p="0.14em"
                  bg="#000"
                  color="white"
                  borderRadius="3px"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M5 3h8.15c3.67 0 6.35 2.49 6.35 5.94 0 3.46-2.68 5.98-6.35 5.98H9.3V21H5V3Zm7.68 8.23c1.48 0 2.49-.91 2.49-2.29 0-1.35-1.01-2.25-2.49-2.25H9.3v4.54h3.38Z" />
                </Box>
                <Text
                  as="a"
                  href="https://www.postype.com/@leta-dg"
                  target="_blank"
                  rel="noopener noreferrer"
                  color={uiTheme.pageSubtleText}
                  fontWeight="700"
                  textDecoration="underline"
                  textUnderlineOffset="3px"
                  _hover={{ color: uiTheme.accent }}
                >
                  @leta-dg
                </Text>
              </HStack>
            </HStack>
          </Box>
          <Text color={uiTheme.pageMutedText} fontSize="sm" maxW="440px" lineHeight="1.7">
            따옴표로 감싼 단어는{' '}
            <Box
              as="span"
              bg="#3a3831"
              color="#d39d00"
              fontSize="13px"
              px="2px"
              textDecoration="none"
              textShadow="0.5px 0 0 #f4cc2e, -0.5px 0 0 #f4cc2e, 0 0.5px 0 #f4cc2e, 0 -0.5px 0 #f4cc2e"
              fontWeight="bold"
            >
              강조 색상
            </Box>
            으로 고정되고,
            <br /> 탭 선택에 따라 일반 대사 색만 바뀝니다.
          </Text>
        </Flex>

        <Grid
          templateColumns={{ base: '1fr', lg: 'minmax(0, 1fr) 112px minmax(0, 1fr)' }}
          gap={{ base: 4, lg: 5 }}
          alignItems="stretch"
        >
          <GridItem>
            <Box className="work-panel">
              <Flex justify="space-between" align="center" gap={3} mb={4}>
                <Tabs
                  index={mode === 'dialogue' ? 0 : 1}
                  onChange={handleSpeechModeChange}
                  variant="unstyled"
                >
                  <TabList className="mode-tabs">
                    <Tab className="mode-tab">대사</Tab>
                    <Tab className="mode-tab">독백</Tab>
                  </TabList>
                </Tabs>
                <HStack spacing={2}>
                  <Badge variant="outline" borderColor={uiTheme.border} color={uiTheme.mutedText} borderRadius="2px">
                    {lineCount} lines
                  </Badge>
                  <Tooltip label="입력 지우기" hasArrow>
                    <Button
                      onClick={() => setInput('')}
                      minW="38px"
                      h="38px"
                      px={0}
                      borderRadius="4px"
                      bg={uiTheme.controlBg}
                      color={uiTheme.mutedText}
                      border={`1px solid ${uiTheme.border}`}
                      fontSize="18px"
                      lineHeight="1"
                      _hover={{ borderColor: uiTheme.accent, color: uiTheme.accent }}
                      _active={{ bg: uiTheme.controlActiveBg }}
                    >
                      ×
                    </Button>
                  </Tooltip>
                </HStack>
              </Flex>

              <FormControl>
                <Flex justify="space-between" align="center" gap={3} mb={2}>
                  <FormLabel color={uiTheme.headingText} fontSize="sm" fontWeight="700" mb={0}>
                    입력
                  </FormLabel>
                  <HStack spacing={2} flexWrap="wrap" justify="flex-end">
                    <Button
                      onClick={() => insertInputText('···')}
                      size="sm"
                      minW="38px"
                      h="32px"
                      px={2}
                      borderRadius="4px"
                      bg={uiTheme.controlBg}
                      color={uiTheme.mutedText}
                      border={`1px solid ${uiTheme.border}`}
                      _hover={{ borderColor: uiTheme.accent, color: uiTheme.accent }}
                      _active={{ bg: uiTheme.controlActiveBg }}
                    >
                      ···
                    </Button>
                    <Button
                      onClick={() => insertInputText('ー')}
                      size="sm"
                      minW="38px"
                      h="32px"
                      px={2}
                      borderRadius="4px"
                      bg={uiTheme.controlBg}
                      color={uiTheme.mutedText}
                      border={`1px solid ${uiTheme.border}`}
                      _hover={{ borderColor: uiTheme.accent, color: uiTheme.accent }}
                      _active={{ bg: uiTheme.controlActiveBg }}
                    >
                      ー
                    </Button>
                    <Button
                      onClick={() => insertInputText('「')}
                      size="sm"
                      minW="38px"
                      h="32px"
                      px={2}
                      borderRadius="4px"
                      bg={uiTheme.controlBg}
                      color={uiTheme.mutedText}
                      border={`1px solid ${uiTheme.border}`}
                      _hover={{ borderColor: uiTheme.accent, color: uiTheme.accent }}
                      _active={{ bg: uiTheme.controlActiveBg }}
                    >
                      「
                    </Button>
                    <Button
                      onClick={() => insertInputText('」')}
                      size="sm"
                      minW="38px"
                      h="32px"
                      px={2}
                      borderRadius="4px"
                      bg={uiTheme.controlBg}
                      color={uiTheme.mutedText}
                      border={`1px solid ${uiTheme.border}`}
                      _hover={{ borderColor: uiTheme.accent, color: uiTheme.accent }}
                      _active={{ bg: uiTheme.controlActiveBg }}
                    >
                      」
                    </Button>
                  </HStack>
                </Flex>
                <Textarea
                  ref={inputRef}
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  minH={{ base: '320px', md: '500px' }}
                  resize="vertical"
                  spellCheck={false}
                  bg={uiTheme.inputBg}
                  borderColor={uiTheme.border}
                  color={uiTheme.text}
                  _hover={{ borderColor: uiTheme.accent }}
                  _focusVisible={{
                    borderColor: uiTheme.accent,
                    boxShadow: `0 0 0 1px ${uiTheme.accent}`,
                  }}
                  placeholder="변환할 대사를 입력하세요."
                />
              </FormControl>
            </Box>
          </GridItem>

          <GridItem>
            <Flex h="100%" align="center" justify="center" direction="column" gap={3}>
              <Tabs
                index={timeMode === 'day' ? 0 : 1}
                onChange={handleTimeModeChange}
                variant="unstyled"
              >
                <TabList className="time-tabs">
                  <Tab className="time-tab">낮</Tab>
                  <Tab className="time-tab">밤</Tab>
                </TabList>
              </Tabs>
              <Button
                onClick={handleConvert}
                w={{ base: '100%', lg: '96px' }}
                h={{ base: '48px', lg: '96px' }}
                bg={uiTheme.accent}
                color={uiTheme.accentText}
                borderRadius="4px"
                fontWeight="900"
                _hover={{ bg: uiTheme.accentHover }}
                _active={{ bg: uiTheme.accentActive }}
              >
                변환
              </Button>
            </Flex>
          </GridItem>

          <GridItem>
            <Box className="work-panel">
              <Flex justify="space-between" align="center" gap={3} mb={4}>
                <Box>
                  <Text color={uiTheme.headingText} fontSize="sm" fontWeight="700">
                    결과
                  </Text>
                </Box>
              </Flex>

              {output && (
                <Box className="preview-panel" mb={4}>
                  <Flex justify="space-between" align="center" gap={3} mb={3}>
                    <Text color={uiTheme.headingText} fontSize="sm" fontWeight="700">
                      미리보기
                    </Text>
                    <HStack spacing={2}>
                      <Text color={uiTheme.subtleText} fontSize="xs">
                        표준
                      </Text>
                      <Switch
                        isChecked={previewDarkMode}
                        onChange={(event) => setPreviewDarkMode(event.target.checked)}
                        size="sm"
                        sx={{
                          'span.chakra-switch__track[data-checked]': {
                            bg: uiTheme.accent,
                          },
                        }}
                      />
                      <Text color={uiTheme.subtleText} fontSize="xs">
                        다크
                      </Text>
                    </HStack>
                  </Flex>
                  <Box
                    className="roll20-preview"
                    bg={previewDarkMode ? '#353535' : '#f1f1f1'}
                  >
                    {previewLines.map((line, lineIndex) => (
                      <Box className="roll20-preview-line" key={`preview-line-${lineIndex}`}>
                        {line.map((segment, segmentIndex) => (
                          <Box
                            as="span"
                            className="roll20-preview-segment"
                            key={`preview-segment-${lineIndex}-${segmentIndex}`}
                            style={styleTextToObject(segment.style)}
                          >
                            {segment.text}
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              <Flex justify="space-between" align="center" gap={3} mb={4}>
                <Box>
                  <Text color={uiTheme.subtleText} fontSize="xs" mt={1}>
                    Roll20 채팅창에 붙여넣을 텍스트
                  </Text>
                </Box>
                <Tooltip label={hasCopied ? '복사됨' : '결과 복사'} hasArrow>
                  <Button
                    onClick={handleCopy}
                    size="sm"
                    borderRadius="4px"
                    bg={uiTheme.controlBg}
                    color={uiTheme.text}
                    border={`1px solid ${uiTheme.border}`}
                    _hover={{ borderColor: uiTheme.accent, bg: uiTheme.controlActiveBg }}
                  >
                    복사
                  </Button>
                </Tooltip>
              </Flex>

              <Textarea
                value={output}
                readOnly
                minH={{ base: '342px', md: '342px' }}
                resize="vertical"
                spellCheck={false}
                bg={uiTheme.resultBg}
                borderColor={uiTheme.border}
                color={uiTheme.text}
                fontFamily="'Courier New', monospace"
                fontSize="13px"
                lineHeight="1.65"
                _hover={{ borderColor: uiTheme.accent }}
                _focusVisible={{
                  borderColor: uiTheme.accent,
                  boxShadow: `0 0 0 1px ${uiTheme.accent}`,
                }}
                placeholder="변환 버튼을 누르면 결과가 표시됩니다."
              />
            </Box>
          </GridItem>
        </Grid>

        {/*<HStack mt={5} spacing={3} color={uiTheme.pageSubtleText} fontSize="xs" flexWrap="wrap">*/}
        {/*  <Text>배경 #3a3831</Text>*/}
        {/*  <Text>테두리 {uiTheme.accent}</Text>*/}
        {/*  <Text>강조 #d39d00</Text>*/}
        {/*  <Text>독백 #77e2f5</Text>*/}
        {/*</HStack>*/}
      </Container>
    </Box>
  );
}

export default App;
